import React from 'react';
import { VALIDATIONS } from '@shared/constants';
import { getJWKS, onTokenUpdate, writeXLSFile } from '@utils';
import { DonutService, RingCentralService } from '@consumers/services';
import { Input, ToastDialog } from '@shared/components';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import LoadingGif from '@assets/icons/spinner-144x144px.gif';
import SuccessIcon from '@assets/icons/done-green.svg';
import ErrorIcon from '@assets/icons/cross-red.svg';
import ErrorSymbol from '@assets/icons/error.svg';
import SystemUpdateAltIcon from '@material-ui/icons/SystemUpdateAlt';
import useStyles from './style';
import DataGrid from '../data-grid';
import UploadSection from '../upload-section';
import Progress from '../progress';

let accountId = null;
const SHEET_HEADERS = {
  INSTRUCTIONS: {
    instructions: 'INSTRUCTIONS',
  },
  NAMING: {
    currentPhoneNumber: 'Current Phone Number',
    phoneNumberName:
      'Phone Number Name (character limit 15 characters - no & symbol)',
  },
  ASSIGNING: {
    extensionNumber: 'Extension Number',
    extensionId: 'ExtensionID',
    currentPhoneNumber: 'Existing Phone Number',
    desiredPhoneNumber: 'Number to Add  (E.164 format)',
    availableNumbers: 'Available Numbers',
    availableNumbersName: 'Available Number Name',
    availableNumbersType: 'Available Number Type',
  },
};
const noop = () => {};
const PAPER_ELEVATION = 4;
const NUMERIC_REG_EXP = /^\d+$/;
const defaultState = {
  uid: '',
  accountName: null,
  isLoading: false,
  isFetchingList: false,
  isUidIncorrect: false,
  isErrorEncountered: false,
  isExported: false,
  jwksToken: null,
  phoneNumberList: [],
  extensionList: [],
  entries: [],
  isSyncing: false,
  showSync: false,
  isRateLimited: false,
  rateLimitDelay: 0,
  progress: 0,
  status: '',
  success: [],
  errors: [],
  isSyncPaused: false,
  isFileUploaded: false,
  isSyncCompleted: false,
  wasSyncPaused: false,
  toastDialog: {
    show: false,
    heading: <></>,
    content: <></>,
    contentActionText: '',
    customIcon: null,
    contentAction: noop,
  },
  errorList: {
    uid: '',
  },
};

const Reassigner = () => {
  const classes = useStyles();
  const [state, setState] = React.useState(defaultState);

  const handleSync = async (phoneNumbers = []) => {
    setState(prevState => ({
      ...prevState,
      isSyncing: true,
      showSync: true,
      progress: 0,
      isSyncPaused: false,
      isSyncCompleted: false,
      success: [],
      errors: [],
      isErrorEncountered: false,
      status: '',
    }));
    for await (let [index, phoneNumber] of phoneNumbers.entries()) {
      let token = null;
      let isSyncPaused = false;
      setState(prevState => {
        token = prevState.jwksToken;
        isSyncPaused = prevState.isSyncPaused;
        return {
          ...prevState,
        };
      });
      let payload = {
        usageType: phoneNumber.extensionId
          ? phoneNumber.usageType !== 'CompanyNumber'
            ? phoneNumber.usageType
            : 'DirectNumber'
          : 'CompanyNumber',
      };
      if (phoneNumber.extensionId) {
        payload = {
          ...payload,
          extension: {
            id: phoneNumber.extensionId,
          },
        };
      }
      if (phoneNumber.type === 'naming') {
        payload = {
          label: phoneNumber.phoneNumberName,
        };
      }
      const response = await RingCentralService.updatePhoneNumber(
        state.uid,
        phoneNumber.phoneNumberId,
        state.jwksToken,
        payload,
        rateLimit => {
          const { delay } = rateLimit;
          setState(prevState => ({
            ...prevState,
            isRateLimited: true,
            rateLimitDelay: Math.ceil(delay / 1000),
          }));
          const rateLimitDelayInterval = setInterval(() => {
            setState(prevState => {
              const rateLimitDelay =
                prevState.rateLimitDelay > 0
                  ? prevState.rateLimitDelay - 1
                  : prevState.rateLimitDelay;
              return { ...prevState, rateLimitDelay };
            });
          }, 1000);

          setTimeout(() => {
            clearInterval(rateLimitDelayInterval);
            setState(prevState => ({
              ...prevState,
              isRateLimited: false,
            }));
          }, delay);
        }
      );

      handleJwksResponse(response, (data, error) => {
        setState(prevState => {
          const isCompleted = index >= phoneNumbers.length - 1;
          let success = prevState.success;
          let errors = prevState.errors;
          if ((error || []).length) {
            let operationType = phoneNumber.type || '';
            operationType =
              operationType.charAt(0).toUpperCase() + operationType.slice(1);
            errors.push({
              message: `${operationType} operation issue in updating Phone Number ${
                phoneNumber.desiredPhoneNumber
              }: ${error[0]?.message ||
                error ||
                'Something went wrong, Please check your internet connection or contact your system administrator.'}`,
              row: index + 1,
            });
          } else {
            success.push({
              message:
                phoneNumber.type === 'naming'
                  ? `${phoneNumber.desiredPhoneNumber} Phone Number Name changed to "${phoneNumber.phoneNumberName}"`
                  : phoneNumber.extensionNumber
                  ? `${phoneNumber.desiredPhoneNumber} moved to Ext. ${phoneNumber.extensionNumber}`
                  : `${phoneNumber.desiredPhoneNumber} changed to Company Number`,
            });
          }
          return {
            ...prevState,
            success,
            errors,
            progress: prevState.progress + 100 / phoneNumbers.length,
            isSyncCompleted: isCompleted,
            isSyncing:
              isCompleted || isSyncPaused ? false : prevState.isSyncing,
            wasSyncPaused: isCompleted ? false : prevState.wasSyncPaused,
            isErrorEncountered: !!(error || []).length
              ? true
              : prevState.isErrorEncountered,
            status: !!prevState.errors.length ? 'Error' : 'Success',
            toastDialog: isCompleted
              ? {
                  show: true,
                  content: !!prevState.errors.length
                    ? 'Errors detected'
                    : 'Success! Your data has been updated.',
                  heading: 'SYNC COMPLETED',
                  contentAction: () => {
                    if (!!prevState.errors.length) {
                      setState(prevState => ({
                        ...prevState,
                        toastDialog: defaultState.toastDialog,
                      }));
                    } else {
                      window.location.reload();
                    }
                  },
                }
              : prevState.toastDialog,
          };
        });
      });
      if (isSyncPaused) {
        break;
      }
    }
  };

  const fetchExtensionList = async (uid, jwksToken) => {
    const response = await RingCentralService.getAllExtensions(uid, jwksToken);
    let result = [];
    handleJwksResponse(response, data => (result = data || []));
    return result;
  };

  const fetchPhoneNumbers = async (uid, jwksToken) => {
    const response = await RingCentralService.getPhoneNumbers(uid, jwksToken);
    let result = [];
    handleJwksResponse(response, data => (result = data || []));
    return result;
  };

  const fetchDevicesList = async (uid, jwksToken) => {
    const response = await RingCentralService.getDevices(uid, jwksToken);
    let result = [];
    handleJwksResponse(response, data => (result = data || []));
    return result;
  };

  const fetchAccountName = async (uid = '') => {
    setState(prevState => ({
      ...prevState,
      isLoading: true,
    }));
    const { data, error } = await DonutService.get((uid || '').trim());
    if (error) {
      setState(prevState => ({
        ...prevState,
        isLoading: false,
        isErrorEncountered: true,
        isUidIncorrect: true,
        toastDialog: {
          show: true,
          content: `Alert: ${Array.isArray(error) ? error[0].message : error}`,
          heading: 'UID Not Found',
          contentActionText: 'Close and Continue',
          customIcon: ErrorSymbol,
          contentAction: () => {
            setState(prevState => ({
              ...prevState,
              toastDialog: defaultState.toastDialog,
            }));
          },
        },
      }));
    } else {
      setState(prevState => ({
        ...prevState,
        isLoading: false,
        accountName: data?.account?.name || defaultState.accountName,
      }));
    }
  };

  const fetchJwksToken = async accountUid => {
    setState(prevState => ({
      ...prevState,
      isLoading: true,
    }));
    const { data, error } = await getJWKS(accountUid);
    if (error) {
      const isUidNotFound =
        Array.isArray(error) && error[0].errors[0].errorCode === 'OAU-222';
      const errorMessage = isUidNotFound
        ? 'Alert: UID not located in Salesforce.'
        : `Error: ${Array.isArray(error) ? error[0].errors[0].message : error}`;

      setState(prevState => ({
        ...prevState,
        isLoading: false,
        isErrorEncountered: true,
        isUidIncorrect: true,
        toastDialog: {
          show: true,
          heading: isUidNotFound ? 'UID Not Found' : 'Report Error...',
          content: errorMessage,
          contentActionText: 'Close and Continue',
          customIcon: ErrorSymbol,
          contentAction: () => {
            setState(prevState => ({
              ...prevState,
              toastDialog: defaultState.toastDialog,
            }));
          },
        },
      }));
    } else {
      if (data) {
        setState(prevState => ({
          ...prevState,
          isLoading: true,
        }));
        onTokenUpdate(async uid => {
          const _accountUid = uid || accountUid;
          const { data, error } = await getJWKS(_accountUid);
          if (!data) {
            setState(prevState => ({
              ...prevState,
              isLoading: false,
            }));
            return false;
          }
          setState(prevState => ({
            ...prevState,
            jwksToken: data,
            isLoading: false,
          }));
          return data;
        });
      }
      setState(prevState => ({
        ...prevState,
        jwksToken: data,
        isLoading: false,
      }));
    }
  };

  const handleJwksResponse = (response = {}, callBack = noop) => {
    const { data, error, paging, token } = response;

    if (error) {
      if (Array.isArray(error) && error[0].errorCode === 'OAU-213') {
        return false;
      }
    }
    callBack(data, error, paging);
    if (token) {
      setState(prevState => ({
        ...prevState,
        jwksToken: token,
      }));
    }
  };

  const handleChange = (event, error) => {
    const { name, value } = event.currentTarget;
    let fieldError = error;
    if (!NUMERIC_REG_EXP.test(value)) {
      fieldError = 'Must be a valid number';
    }
    accountId = null;
    setState(prevState => {
      return {
        ...defaultState,
        [name]: value,
        errorList: {
          ...prevState.errorList,
          [name]: fieldError,
        },
      };
    });
  };

  const handleExport = (entries = []) => {
    const assigningSheet = entries.map(entry => ({
      ExtensionID: entry.extensionId,
      'Extension Number': entry.extensionNumber,
      'Existing Phone Number': entry.phoneNumber,
      'Number to Add  (E.164 format)': '',
      '': null,
      'Available Numbers': entry.phoneNumber,
      'Available Number Name': entry.phoneNumberName,
      'Available Number Type': entry.phoneNumberType,
    }));
    const namingSheet = entries.map(entry => ({
      'Current Phone Number': '',
      'Phone Number Name (character limit 15 characters - no & symbol)': '',
    }));
    if (!assigningSheet.length) {
      return setState(prevState => ({
        ...prevState,
        isErrorEncountered: true,
        toastDialog: {
          show: true,
          content: `Error: No results found for filter parameters`,
          heading: 'Report Error',
        },
      }));
    }
    const instructionSheet = [
      '',
      'Naming Tab:',
      'Add number from your available numbers (found on assign tab column F) to column A',
      'Add name in column B',
      'Save and upload to sync',
      '',
      '',
      'Assigning Tab (Existing Data):',
      'Copy desired numbers from column F (available numbers) to the target extension in column D (number to add) \nmake sure you are assigning to the correct extension number',
      'Save and upload to sync',
    ].map(item => ({ INSTRUCTIONS: item }));
    const sheetData = [
      {
        data: instructionSheet,
        name: 'Instructions',
        columnConfig: [{ type: '!cols', value: [{ width: 102 }] }],
      },
      {
        data: namingSheet,
        name: 'Naming',
        columnConfig: [
          { type: '!cols', value: [{ width: 24 }, { width: 64 }] },
        ],
      },
      {
        data: assigningSheet,
        name: 'Assigning',
        columnConfig: [
          {
            type: '!cols',
            value: [
              { width: 20 },
              { width: 20 },
              { width: 24 },
              { width: 30 },
              { width: 12 },
              { width: 20 },
              { width: 24 },
              { width: 24 },
            ],
          },
        ],
      },
    ];
    writeXLSFile(`reassigner-number-${state.uid}.xlsx`, sheetData, {
      bookType: 'xlsx',
      raw: false,
    });
    setState(prevState => ({
      ...prevState,
      isExported: true,
    }));
  };

  const getEntries = (extensions = [], phoneNumbers = []) => {
    const result = [];
    const assignedExtensionIds = [];
    phoneNumbers.map(number => {
      number.extension && assignedExtensionIds.push(number.extension.id);
      result.push({
        extensionId: number?.extension?.id || null,
        extensionNumber: number?.extension?.extensionNumber || null,
        phoneNumber: number.phoneNumber,
        phoneNumberName: number.label,
        phoneNumberType: number.type,
      });
    });

    extensions
      .filter(extension => !assignedExtensionIds.includes(extension.id))
      .map(extension => {
        result.push({
          extensionId: extension.id,
          extensionNumber: extension.extensionNumber,
          phoneNumber: null,
          phoneNumberName: null,
        });
      });
    return result;
  };

  const handleDonwload = async (uid, token) => {
    setState(prevState => ({
      ...prevState,
      isFetchingList: true,
    }));
    const extensionList = await fetchExtensionList(uid, token);
    let phoneNumberList = await fetchPhoneNumbers(uid, token);
    const deviceList = await fetchDevicesList(uid, token);
    const devicesPhoneIds = deviceList.reduce((acc, item) => {
      (item.phoneLines || []).map(phone => {
        if (phone?.phoneInfo?.id) {
          acc.push(phone.phoneInfo.id);
        }
      });
      return acc;
    }, []);
    phoneNumberList = phoneNumberList.filter(
      phone => !devicesPhoneIds.includes(phone.id)
    );
    const entries = getEntries(extensionList, phoneNumberList);
    handleExport(entries);
    setState(prevState => ({
      ...prevState,
      phoneNumberList,
      extensionList,
      isFetchingList: false,
    }));
  };

  const getInputAdorment = (
    isLoading = false,
    isUidIncorrect = false,
    accountName
  ) => {
    if (!isLoading && !isUidIncorrect && !accountName) {
      return null;
    }
    return (
      <img
        alt='Account UID State Icon'
        src={isLoading ? LoadingGif : isUidIncorrect ? ErrorIcon : SuccessIcon}
        width={isUidIncorrect ? 18 : 26}
        height={isUidIncorrect ? 18 : 26}
      />
    );
  };

  React.useEffect(() => {
    if (!!state.jwksToken) {
      // fetchAccountName(state.uid);
      setState(prevState => ({
        ...prevState,
        isLoading: false,
        accountName: '',
      }));
    }
  }, [state.jwksToken]);

  return (
    <>
      <Paper
        className='p-6 mr-10 ml-10 d-flex f-justify-between'
        elevation={PAPER_ELEVATION}
      >
        <div className='w-75'>
          <div className='d-flex f-justify-between f-align-center '>
            <div>
              <Typography variant='h5' className='medium-font mb-2'>
                Phone Number and Extension Reassigner
              </Typography>
              <Typography variant='caption' className='color-text-secondary'>
                This app lets you assign phone numbers to extensions or as
                company numbers.
              </Typography>
            </div>
          </div>
          <Typography variant='body1' className='bold-font mt-8'>
            Enter the UID
          </Typography>
          <div className='d-flex f-align-center'>
            <div className='w-25'>
              <Input
                fullWidth
                type='text'
                name='uid'
                placeholder='Target Account (UID)'
                className='mb-4'
                hasError={!!state.errorList.uid}
                helperText={state.errorList.uid}
                InputProps={{
                  endAdornment: getInputAdorment(
                    state.isLoading,
                    state.isUidIncorrect,
                    state.accountName
                  ),
                }}
                validations={{
                  [VALIDATIONS.MAX_LENGTH]: 19,
                  [VALIDATIONS.REQUIRED]: true,
                }}
                value={
                  !!state.accountName
                    ? `${state.uid} - ${state.accountName}`
                    : `${state.uid}`
                }
                onChange={handleChange}
                onKeyDown={event => {
                  const { value } = event.target;
                  if (parseInt(event.keyCode) === 13) {
                    if (
                      !value ||
                      accountId === value ||
                      !!state.errorList.uid ||
                      state.jwksToken
                    ) {
                      return;
                    }
                    fetchJwksToken(value);
                    accountId = value;
                  }
                }}
                onBlur={event => {
                  const { value } = event.currentTarget;
                  if (
                    !value ||
                    accountId === value ||
                    !!state.errorList.uid ||
                    state.jwksToken
                  ) {
                    return;
                  }
                  fetchJwksToken(value);
                  accountId = value;
                }}
              />
            </div>
          </div>
        </div>
        <div>
          {!!state.jwksToken && (
            <Button
              disabled={
                state.isFetchingList || state.isSyncing || state.isFileUploaded
              }
              color='primary'
              variant='contained'
              startIcon={<SystemUpdateAltIcon />}
              onClick={() => handleDonwload(state.uid, state.jwksToken)}
            >
              Download
              {state.isFetchingList && (
                <CircularProgress size={24} className='p-absolute color-grey' />
              )}
            </Button>
          )}
        </div>
      </Paper>
      {state.isExported && (
        <UploadSection
          paperElevation={PAPER_ELEVATION}
          sheetHeaders={SHEET_HEADERS}
          phoneNumberList={state.phoneNumberList}
          extensionList={state.extensionList}
          isSyncing={state.isSyncing}
          onUpload={entries => {
            setState(prevState => ({
              ...prevState,
              entries,
              isFileUploaded: true,
              isSyncCompleted: false,
              isSyncPaused: false,
              isSyncing: false,
              progress: 0,
              success: [],
              errors: [],
              isErrorEncountered: false,
              status: '',
            }));
          }}
        />
      )}
      {state.showSync && (
        <Progress
          success={state.success}
          errors={state.errors}
          paperElevation={PAPER_ELEVATION}
          progressPercent={state.progress}
          isRateLimited={state.isRateLimited}
          rateLimitDelay={state.rateLimitDelay}
          isSyncing={state.isSyncing}
          hasPaused={state.isSyncPaused}
          onSyncPause={() => {
            setState(prevState => ({
              ...prevState,
              isSyncPaused: true,
              wasSyncPaused: true,
            }));
          }}
        />
      )}
      {!!state.entries.length && (
        <DataGrid
          paperElevation={PAPER_ELEVATION}
          entries={state.entries}
          isSyncing={state.isSyncing}
          isSyncCompleted={state.isSyncCompleted}
          isSyncPaused={state.isSyncPaused}
          wasSyncPaused={state.wasSyncPaused}
          onSync={() => handleSync(state.entries)}
        />
      )}
      {state.toastDialog.show && (
        <ToastDialog
          status={state.status}
          open={state.toastDialog.show}
          heading={state.toastDialog.heading}
          contentText={state.toastDialog.content}
          contentAction={state.toastDialog.contentAction}
          contentActionText={state.toastDialog.contentActionText}
          customIcon={state.toastDialog.customIcon}
          isError={state.isErrorEncountered}
          onClose={() => {
            setState(prevState => ({
              ...prevState,
              isErrorEncountered: defaultState.isErrorEncountered,
              toastDialog: {
                ...defaultState.toastDialog,
              },
            }));
          }}
        />
      )}
    </>
  );
};

export default Reassigner;
